// 订单搜索逻辑封装

import { useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchOrders } from '@/shared/api/orderApi';
import { useResponsiveParams } from '@/shared/hooks/useResponsiveParams';
import type { SearchOrdersParams } from '@/shared/api/orderApi';
import type { ExtendedSearchOrdersParams } from '../types';
import { transformSearchParams } from '../utils/orderUtils';

export const useOrderSearch = (
  initialParams: ExtendedSearchOrdersParams,
  options?: {
    enabled?: boolean;
    onParamsChange?: (params: ExtendedSearchOrdersParams) => void;
  }
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 从URL解析参数
  const urlParams = useMemo(() => {
    const params: Partial<ExtendedSearchOrdersParams> = {};
    const customer_id = searchParams.get('customer_id');
    const status = searchParams.get('status');
    const start_date = searchParams.get('start_date');
    const end_date = searchParams.get('end_date');
    const limit = searchParams.get('limit');
    const offset = searchParams.get('offset');

    if (customer_id) params.customer_id = customer_id;
    if (status) params.status = status;
    if (start_date) params.start_date = start_date;
    if (end_date) params.end_date = end_date;
    if (limit) params.limit = parseInt(limit, 10);
    if (offset) params.offset = parseInt(offset, 10);

    return params;
  }, [searchParams]);

  // 合并初始参数和URL参数
  const mergedInitialParams = useMemo(() => ({
    ...initialParams,
    ...urlParams,
  }), [initialParams, urlParams]);

  // 同步参数到URL
  const syncParamsToUrl = useCallback((params: ExtendedSearchOrdersParams) => {
    const newSearchParams = new URLSearchParams();

    // 只同步后端支持的筛选字段
    const supportedFields = ['customer_id', 'status', 'start_date', 'end_date', 'limit', 'offset'];

    Object.entries(params).forEach(([key, value]) => {
      // 只同步有值且在后端支持字段列表中的参数
      if (value !== undefined && value !== null && value !== '' && supportedFields.includes(key)) {
        newSearchParams.set(key, String(value));
      }
    });

    // 始终包含company_id
    newSearchParams.set('company_id', String(params.company_id || initialParams.company_id));

    // 只有当URL参数确实变化时才更新，避免不必要的重渲染
    const currentUrl = new URLSearchParams(searchParams);
    if (currentUrl.toString() !== newSearchParams.toString()) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, initialParams.company_id]);

  // 使用响应式参数Hook，监听initialParams变化
  const {
    params,
    updateParams: internalUpdateParams,
    resetParams,
  } = useResponsiveParams(mergedInitialParams, (updatedParams) => {
    // 同步到URL
    syncParamsToUrl(updatedParams);
    // 调用外部回调（如果提供）
    options?.onParamsChange?.(updatedParams);
  });

  // 转换参数为后端格式
  const backendParams = useMemo(() => {
    return transformSearchParams(params);
  }, [params]);

  // 使用现有的搜索hook，支持enabled控制
  const queryOptions = {
    enabled: options?.enabled ?? true,
  };

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useSearchOrders(backendParams as SearchOrdersParams, queryOptions);

  // 增强的updateParams，支持分页重置逻辑
  const updateParams = useCallback(
    (newParams: Partial<ExtendedSearchOrdersParams>) => {
      const updated = internalUpdateParams({
        ...newParams,
        offset: 0, // 自动重置到第一页
      });
      return updated;
    },
    [internalUpdateParams]
  );

  // 处理分页变化
  const handlePageChange = useCallback((page: number, pageSize: number) => {
    internalUpdateParams({
      offset: (page - 1) * pageSize,
      limit: pageSize,
    });
  }, [internalUpdateParams]);

  // 处理每页显示数量变化
  const handlePageSizeChange = useCallback((current: number, size: number) => {
    internalUpdateParams({
      limit: size,
      offset: 0, // 重置到第一页
    });
  }, [internalUpdateParams]);

  // 应用快速筛选
  const applyQuickFilter = useCallback((filterType: string) => {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    switch (filterType) {
      case 'today':
        updateParams({
          start_date: startOfDay.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        });
        break;

      case 'pending':
        updateParams({ status: 'pending' });
        break;

      case 'processing':
        updateParams({ status: 'processing' });
        break;

      case 'cancelled':
        updateParams({ status: 'cancelled' });
        break;

      case 'delivered':
        updateParams({ status: 'delivered' });
        break;

      case 'recent':
        updateParams({
          start_date: sevenDaysAgo.toISOString().split('T')[0],
          end_date: today.toISOString().split('T')[0],
        });
        break;



      default:
        break;
    }
  }, [updateParams]);

  // 清除特定筛选条件
  const clearFilter = useCallback((filterKey: keyof ExtendedSearchOrdersParams) => {
    internalUpdateParams({
      [filterKey]: undefined,
    } as Partial<ExtendedSearchOrdersParams>);
  }, [internalUpdateParams]);

  // 清除所有筛选条件
  const clearAllFilters = useCallback(() => {
    // 清除所有筛选字段，保留基础字段（company_id, limit），重置offset为0
    internalUpdateParams({
      customer_id: undefined,
      status: undefined,
      start_date: undefined,
      end_date: undefined,
      // 扩展字段（前端专用，不影响后端API，但也要清除）
      order_id: undefined,
      customer_name: undefined,
      customer_email: undefined,
      customer_phone: undefined,
      min_amount: undefined,
      max_amount: undefined,
      platform: undefined,
      sort_by: undefined,
      sort_order: undefined,
      offset: 0, // 重置到第一页
    });
  }, [internalUpdateParams]);

  // 获取当前分页信息
  const paginationInfo = useMemo(() => {
    const currentPage = Math.floor((params.offset || 0) / (params.limit || 20)) + 1;
    const pageSize = params.limit || 20;
    const total = data?.data?.total_count || 0;
    const totalPages = Math.ceil(total / pageSize);
    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, total);

    return {
      currentPage,
      pageSize,
      total,
      totalPages,
      startIndex,
      endIndex,
      hasPrevious: currentPage > 1,
      hasNext: currentPage < totalPages,
    };
  }, [params.offset, params.limit, data?.data?.total_count]);

  // 获取活跃的筛选条件
  const activeFilters = useMemo(() => {
    const filters: Array<{
      key: keyof ExtendedSearchOrdersParams;
      label: string;
      value: any;
      displayValue: string;
    }> = [];

    const filterLabels: Record<keyof ExtendedSearchOrdersParams, string> = {
      company_id: '公司ID',
      customer_id: '客户ID',
      status: '状态',
      start_date: '开始日期',
      end_date: '结束日期',
      // 后端API仅支持以上字段
      limit: '每页数量',
      offset: '偏移量',
      sort_by: '排序字段',
      sort_order: '排序方向',
      // 前端扩展字段
      order_id: '订单ID',
      customer_name: '客户姓名',
      customer_email: '客户邮箱',
      customer_phone: '客户电话',
      min_amount: '最小金额',
      max_amount: '最大金额',
      platform: '平台',
    };

    Object.entries(params).forEach(([key, value]) => {
      // 排除非业务筛选字段：公司ID、分页参数、排序参数
      const excludedKeys = ['company_id', 'limit', 'offset', 'sort_by', 'sort_order'];
      if (value !== undefined && value !== null && value !== '' && !excludedKeys.includes(key)) {
        const filterKey = key as keyof ExtendedSearchOrdersParams;
        const label = filterLabels[filterKey] || filterKey;

        // 格式化显示值
        let displayValue = value;
        if (filterKey === 'status') {
          const statusTexts: Record<string, string> = {
            pending: '待处理',
            processing: '处理中',
            shipped: '已发货',
            delivered: '已送达',
            cancelled: '已取消',
          };
          displayValue = statusTexts[value as string] || value;
        }

        filters.push({
          key: filterKey,
          label,
          value,
          displayValue: String(displayValue),
        });
      }
    });

    return filters;
  }, [params]);

  return {
    // 状态
    params,
    data,
    isLoading: isLoading || isFetching,
    error,

    // 数据
    orders: data?.data?.orders || [],
    totalCount: data?.data?.total_count || 0,

    // 分页信息
    paginationInfo,

    // 筛选信息
    activeFilters,

    // 操作方法
    updateParams,
    resetParams,
    refetch,
    handlePageChange,
    handlePageSizeChange,
    applyQuickFilter,
    clearFilter,
    clearAllFilters,
  };
};